import { gql, ApolloError } from '@apollo/client';
import { apolloClient } from '../apollo/client';
import type { Cupom, CupomInput, CupomConnection, TipoCupom, StatusCupom } from '../types/cupom';

export type { Cupom, CupomInput, CupomConnection, TipoCupom, StatusCupom } from '../types/cupom';

export const CUPONS_QUERY = gql`
  query Cupons(
    $codigo: String
    $page: Int
    $size: Int
    $status: StatusCupom
    $tipo: TipoCupom
    $torneioId: ID
  ) {
    cupons(
      codigo: $codigo
      page: $page
      size: $size
      status: $status
      tipo: $tipo
      torneioId: $torneioId
    ) {
      page
      size
      totalItems
      totalPages
      items {
        atualizadoEm
        codigo
        criadoEm
        dataExpiracao
        quantidadeDisponivel
        status
        tipo
        valor
        torneio {
          id
          nome
        }
      }
    }
  }
`;

export const CUPOM_POR_CODIGO_QUERY = gql`
  query CupomPorCodigo($codigo: String!) {
    cupomPorCodigo(codigo: $codigo) {
      codigo
      tipo
      valor
      quantidadeDisponivel
      status
      torneio { id }
      dataExpiracao
      criadoEm
      atualizadoEm
    }
  }
`;

export const CRIAR_CUPOM_MUTATION = gql`
  mutation CriarCupom($input: CupomInput!) {
    criarCupom(input: $input) {
      codigo
      tipo
      valor
      quantidadeDisponivel
      status
      torneio { id }
      dataExpiracao
      criadoEm
      atualizadoEm
    }
  }
`;

export const ATUALIZAR_CUPOM_MUTATION = gql`
  mutation AtualizarCupom($codigo: String!, $input: CupomInput!) {
    atualizarCupom(codigo: $codigo, input: $input) {
      codigo
      tipo
      valor
      quantidadeDisponivel
      status
      torneio { id }
      dataExpiracao
      criadoEm
      atualizadoEm
    }
  }
`;

function getErrorMessage(error: unknown): string {
  if (error instanceof ApolloError) {
    return error.graphQLErrors[0]?.message || error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'Erro inesperado.';
}

export async function listarCupons(
  params?: {
    page?: number;
    size?: number;
    codigo?: string;
    status?: StatusCupom;
    tipo?: TipoCupom;
    torneioId?: string;
  },
): Promise<CupomConnection> {
  try {
    const { data } = await apolloClient.query<
      { cupons: CupomConnection },
      {
        page?: number;
        size?: number;
        codigo?: string;
        status?: StatusCupom;
        tipo?: TipoCupom;
        torneioId?: string;
      }
    >(
      {
        query: CUPONS_QUERY,
        variables: {
          page: params?.page,
          size: params?.size,
          codigo: params?.codigo,
          status: params?.status,
          tipo: params?.tipo,
          torneioId: params?.torneioId,
        },
        fetchPolicy: 'no-cache',
      },
    );
    return data.cupons;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
}

export async function buscarCupomPorCodigo(codigo: string): Promise<Cupom | null> {
  try {
    const { data } = await apolloClient.query<{ cupomPorCodigo: Cupom | null }, { codigo: string }>({
      query: CUPOM_POR_CODIGO_QUERY,
      variables: { codigo },
      fetchPolicy: 'no-cache',
    });
    return data.cupomPorCodigo;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
}

export async function criarCupom(input: CupomInput): Promise<Cupom> {
  try {
    const { data } = await apolloClient.mutate<{ criarCupom: Cupom }, { input: CupomInput }>({
      mutation: CRIAR_CUPOM_MUTATION,
      variables: { input },
    });
    if (!data?.criarCupom) {
      throw new Error('Erro ao criar cupom.');
    }
    return data.criarCupom;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
}

export async function atualizarCupom(
  codigo: string,
  input: CupomInput,
): Promise<Cupom> {
  try {
    const { data } = await apolloClient.mutate<{ atualizarCupom: Cupom }, { codigo: string; input: CupomInput }>(
      {
        mutation: ATUALIZAR_CUPOM_MUTATION,
        variables: { codigo, input },
      },
    );
    if (!data?.atualizarCupom) {
      throw new Error('Erro ao atualizar cupom.');
    }
    return data.atualizarCupom;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
}

